import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import {
  submitAllocationFeedback,
  type AllocationFeedbackResult,
  type WorkOption,
} from "../../faturas/faturasApi";
import { APP_LOCALE } from "../../config/locale";
import {
  SearchableSelect,
  type SearchableSelectOption,
} from "../../components/SearchableSelect";

interface AllocationFeedbackControlProps {
  allocationId: number;
  feedbackTypeCode?: string | null;
  feedbackErrorDescription?: string | null;
  feedbackCorrectedWorkId?: number | null;
  feedbackAt?: string | null;
  works: WorkOption[];
  onSaved: (feedback: AllocationFeedbackResult) => void;
  onError: (message: string) => void;
}

/** Largura desejada do popover e folga mínima às margens do viewport, em px. */
const PANEL_WIDTH = 448;
const PANEL_MARGIN = 8;

interface PanelPosition {
  top: number;
  left: number;
  width: number;
}

/**
 * Botões 👍/👎 de uma linha de repartição, com gravação imediata (sem Save global). O 👎 abre um
 * popover com a explicação obrigatória (é o texto que treina a IA) e a obra correta opcional.
 * Partilhado pelos três sítios onde a repartição aparece: linha expandida, modal e editor.
 */
export function AllocationFeedbackControl({
  allocationId,
  feedbackTypeCode,
  feedbackErrorDescription,
  feedbackCorrectedWorkId,
  feedbackAt,
  works,
  onSaved,
  onError,
}: AllocationFeedbackControlProps) {
  const [current, setCurrent] = useState<string | null>(feedbackTypeCode ?? null);
  const [currentAt, setCurrentAt] = useState<string | null>(feedbackAt ?? null);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [description, setDescription] = useState(feedbackErrorDescription ?? "");
  const [correctedWorkId, setCorrectedWorkId] = useState(
    feedbackCorrectedWorkId ? String(feedbackCorrectedWorkId) : "",
  );
  const [isSaving, setIsSaving] = useState(false);
  const [panelPosition, setPanelPosition] = useState<PanelPosition | null>(null);
  const notOkButtonRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Re-sinc quando o pai recarrega a fatura (props novas para a mesma linha) — inclui fechar o
  // popover, para o estado transiente não sobreviver a uma troca de allocationId (React reutiliza
  // a instância quando a key do pai se mantém).
  useEffect(() => {
    setCurrent(feedbackTypeCode ?? null);
    setCurrentAt(feedbackAt ?? null);
    setDescription(feedbackErrorDescription ?? "");
    setCorrectedWorkId(feedbackCorrectedWorkId ? String(feedbackCorrectedWorkId) : "");
    setIsPanelOpen(false);
  }, [allocationId, feedbackTypeCode, feedbackErrorDescription, feedbackCorrectedWorkId, feedbackAt]);

  // Fechar devolve sempre o foco ao 👎 (senão cai no <body> e o utilizador de teclado perde o
  // sítio) e descarta o rascunho — o que fica visível é sempre o que está persistido.
  const closePanel = () => {
    setDescription(feedbackErrorDescription ?? "");
    setCorrectedWorkId(feedbackCorrectedWorkId ? String(feedbackCorrectedWorkId) : "");
    setIsPanelOpen(false);
    notOkButtonRef.current?.focus();
  };

  // O popover vai para um portal com posição fixa: ancorado na linha ficava preso aos ancestrais
  // com overflow (a lista e o modal do documento) e era cortado. O preço é ter de recalcular a
  // posição — na abertura, no scroll de qualquer ancestral (capture) e no resize.
  const updatePosition = useCallback(() => {
    const button = notOkButtonRef.current;
    const panel = panelRef.current;
    if (!button || !panel) return;

    const anchor = button.getBoundingClientRect();
    const width = Math.min(PANEL_WIDTH, window.innerWidth - PANEL_MARGIN * 2);
    const height = panel.offsetHeight;

    const left = Math.min(
      Math.max(anchor.right - width, PANEL_MARGIN),
      Math.max(window.innerWidth - width - PANEL_MARGIN, PANEL_MARGIN),
    );

    // Por baixo do botão; se não couber lá e couber por cima, vira para cima.
    let top = anchor.bottom + 4;
    if (top + height > window.innerHeight - PANEL_MARGIN && anchor.top - height - 4 >= PANEL_MARGIN) {
      top = anchor.top - height - 4;
    }
    top = Math.max(PANEL_MARGIN, Math.min(top, window.innerHeight - height - PANEL_MARGIN));

    setPanelPosition({ top, left, width });
  }, []);

  useLayoutEffect(() => {
    if (!isPanelOpen) {
      setPanelPosition(null);
      return;
    }

    updatePosition();
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);
    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [isPanelOpen, updatePosition]);

  // Abrir um role="dialog" tem de levar o foco lá para dentro — senão o leitor de ecrã não o
  // anuncia e quem usa teclado fica no botão, fora do painel.
  useEffect(() => {
    if (isPanelOpen) {
      textareaRef.current?.focus();
    }
  }, [isPanelOpen]);

  const savedTitle = (verdict: string): string => {
    const when = currentAt
      ? ` em ${new Date(currentAt).toLocaleDateString(APP_LOCALE)}`
      : "";
    return verdict === "ok"
      ? `Distribuição confirmada${when}`
      : `Distribuição marcada como errada${when}`;
  };

  const submit = async (payload: {
    feedbackTypeCode: "ok" | "not-ok";
    errorDescription?: string | null;
    correctedWorkId?: number | null;
  }) => {
    setIsSaving(true);
    try {
      const result = await submitAllocationFeedback({ allocationId, ...payload });
      setCurrent(result.feedbackTypeCode);
      setCurrentAt(result.feedbackAt);
      setDescription(result.errorDescription ?? "");
      setCorrectedWorkId(result.correctedWorkId ? String(result.correctedWorkId) : "");
      if (isPanelOpen) {
        setIsPanelOpen(false);
        notOkButtonRef.current?.focus();
      }
      onSaved(result);
    } catch (error) {
      onError(error instanceof Error ? error.message : String(error));
    } finally {
      setIsSaving(false);
    }
  };

  // Com veredicto dado, o contraste faz-se pelos dois lados: o escolhido ganha um fundo suave da
  // cor do veredicto (borda subtil a acompanhar); o preterido cai para cinza esbatido. Sem
  // veredicto, ambos neutros.
  const buttonStyle = (active: boolean, accentColor: string): React.CSSProperties => {
    const hasVerdict = current !== null;
    return {
      borderRadius: "var(--radius-button)",
      lineHeight: 1,
      padding: "0.125rem 0.375rem",
      opacity: active ? 1 : hasVerdict ? 0.35 : 0.7,
      filter: !active && hasVerdict ? "grayscale(1)" : "none",
      border: active
        ? `1px solid color-mix(in srgb, ${accentColor} 35%, transparent)`
        : "1px solid transparent",
      background: active ? `color-mix(in srgb, ${accentColor} 12%, transparent)` : "transparent",
      cursor: isSaving ? "wait" : "pointer",
    };
  };

  const workOptions = useMemo<SearchableSelectOption<string>[]>(
    () => [
      { value: "", label: "— Nenhuma / não sei —" },
      ...works.map((work) => ({
        value: String(work.id),
        label: work.name,
        keywords: work.code,
      })),
    ],
    [works],
  );

  const panelId = `allocation-feedback-panel-${allocationId}`;

  const panel = (
    <div
      id={panelId}
      ref={panelRef}
      role="dialog"
      aria-label="Marcar distribuição como errada"
      className="bg-card border border-border shadow-lg flex flex-col gap-3 p-4"
      style={{
        position: "fixed",
        // Antes de medir a altura não há posição fiável — renderiza-se invisível (mas com layout)
        // para poder medir sem o painel piscar no sítio errado.
        top: panelPosition?.top ?? 0,
        left: panelPosition?.left ?? 0,
        width: panelPosition?.width ?? PANEL_WIDTH,
        visibility: panelPosition ? "visible" : "hidden",
        zIndex: 80,
        borderRadius: "var(--radius)",
      }}
    >
      <label className="flex flex-col gap-1 text-left">
        <span className="text-xs text-secondary-text">
          O que está errado? (obrigatório — usado para treinar a IA)
        </span>
        <textarea
          ref={textareaRef}
          rows={5}
          value={description}
          onChange={(event) => setDescription(event.target.value)}
          maxLength={1000}
          required
          aria-required="true"
          placeholder="Ex.: combustíveis nunca são imputados a obra"
          className="px-3 py-2 border border-border rounded bg-background text-foreground text-sm"
          style={{ borderRadius: "var(--radius-button)", resize: "vertical" }}
        />
      </label>
      <SearchableSelect<string>
        className="text-left"
        label="Obra correta (opcional)"
        labelClassName="text-xs text-secondary-text"
        labelStyle={{}}
        value={correctedWorkId}
        onChange={(next) => setCorrectedWorkId(next ?? "")}
        options={workOptions}
        allowClear={false}
        searchPlaceholder="Procurar obra…"
      />
      <div className="flex items-center justify-end gap-2">
        <button
          type="button"
          disabled={isSaving}
          onClick={closePanel}
          className="px-3 py-1.5 border border-border rounded text-sm text-secondary-text"
          style={{ borderRadius: "var(--radius-button)", opacity: isSaving ? 0.5 : 1 }}
        >
          Cancelar
        </button>
        <button
          type="button"
          disabled={isSaving || description.trim().length === 0}
          onClick={() =>
            submit({
              feedbackTypeCode: "not-ok",
              errorDescription: description.trim(),
              correctedWorkId: correctedWorkId ? Number(correctedWorkId) : null,
            })
          }
          className="px-3 py-1.5 rounded text-sm"
          style={{
            borderRadius: "var(--radius-button)",
            background: "var(--destructive)",
            color: "var(--destructive-foreground)",
            opacity: isSaving || description.trim().length === 0 ? 0.5 : 1,
          }}
        >
          Guardar
        </button>
      </div>
    </div>
  );

  return (
    // Escape no wrapper e não no painel: os eventos do portal borbulham pela árvore React, por isso
    // este handler apanha o painel E os botões — no painel, uma tecla premida no 👎 (que é irmão,
    // não descendente) nunca lá chegava, e o Escape estava morto até se entrar no painel.
    <span
      className="relative inline-flex items-center gap-1"
      onKeyDown={(event) => {
        if (event.key === "Escape" && isPanelOpen) {
          // Com a lista do seletor de obra aberta o Escape nunca chega aqui: o
          // SearchableSelect trava-o para fechar só a lista e não o painel inteiro.
          event.stopPropagation();
          closePanel();
        }
      }}
    >
      <button
        type="button"
        aria-label="Distribuição correta"
        aria-pressed={current === "ok"}
        title={current === "ok" ? savedTitle("ok") : "Marcar distribuição como correta"}
        disabled={isSaving}
        onClick={() => submit({ feedbackTypeCode: "ok" })}
        style={buttonStyle(current === "ok", "var(--primary)")}
      >
        <span aria-hidden="true">👍</span>
      </button>
      <button
        type="button"
        ref={notOkButtonRef}
        aria-label="Distribuição errada"
        aria-pressed={current === "not-ok"}
        aria-expanded={isPanelOpen}
        aria-controls={isPanelOpen ? panelId : undefined}
        title={current === "not-ok" ? savedTitle("not-ok") : "Marcar distribuição como errada"}
        disabled={isSaving}
        onClick={() => (isPanelOpen ? closePanel() : setIsPanelOpen(true))}
        style={buttonStyle(current === "not-ok", "var(--destructive)")}
      >
        <span aria-hidden="true">👎</span>
      </button>

      {isPanelOpen && createPortal(panel, document.body)}
    </span>
  );
}
